import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Headphones, BookOpen, Check, ShieldCheck, Clock, Volume2, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAudio } from "@/lib/useAudio";
import { playChime, unlockAudioContext, haptic } from "@/lib/sound";
import { Loader, RichText } from "@/components/common";
import AudioBar from "@/components/AudioBar";
import { actionLink } from "@/pages/Home";

function ModeToggle({ mode, onChange, t }) {
  return (
    <div className="flex rounded-full bg-[#F5F0E6] p-1 text-[10px] font-bold" data-testid="lesson-mode-toggle">
      {[["audio", Headphones, "AUDIO ON"], ["read", BookOpen, "READ ONLY"]].map(([k, Icon, l]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`lesson-mode-${k}`}
          className={`flex items-center gap-1 rounded-full px-2.5 h-8 transition-colors ${mode === k ? "bg-[#1E2B3C] text-white" : "text-[#5C6B73]"}`}><Icon className="h-3.5 w-3.5" />{t(l)}</button>
      ))}
    </div>
  );
}

function Exercise({ ex, value, onChange, t }) {
  return (
    <div className="rounded-2xl border-2 border-[#1DB99A] bg-[#E8F8F4]/60 p-4" data-testid="lesson-exercise">
      <div className="sb-eyebrow text-[#179B80]">Action · {ex.label}</div>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={t("My answer...")} data-testid="exercise-answer-input"
        onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 300)}
        className="mt-2 w-full rounded-xl border border-[#EADEC9] bg-white p-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1DB99A]" />
    </div>
  );
}

function SaveAnswerButton({ value, saved, onSave, busy, t }) {
  const isSaved = saved && value.trim() === saved;
  return (
    <button onClick={onSave} disabled={busy || !value.trim() || isSaved} data-testid="exercise-save-btn"
      className={`sb-btn w-full text-sm ${isSaved ? "bg-[#E8F8F4] text-[#179B80]" : "bg-[#1DB99A] text-white hover:bg-[#179B80]"}`}>
      {isSaved ? <><Check className="h-4 w-4" /> {t("Saved to your business")}</> : t("SAVE TO MY BUSINESS")}
    </button>
  );
}

function Celebration({ children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-[#1E2B3C]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid={testid}>
      <div className="w-full max-w-md rounded-3xl bg-[#FBF8F2] p-7 text-center pop-in pb-safe">{children}</div>
    </div>
  );
}

export default function Lesson() {
  const { lessonId } = useParams();
  const { user, updateProfile, t } = useAuth();
  const nav = useNavigate();
  const endedRef = useRef(null);
  const audio = useAudio(() => endedRef.current?.());
  const [auto, setAuto] = useState(false);
  const [L, setL] = useState(null);
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [mode, setMode] = useState(user.audio_mode === "read" ? "read" : "audio");
  const [answer, setAnswer] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [stageCeleb, setStageCeleb] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setL(null); setStarted(false); setIdx(0); setDone(null); setStageCeleb(false);
    api.get(`/lessons/${lessonId}`).then((r) => {
      setL(r.data);
      setAnswer(r.data.answer || "");
      setSaved(r.data.answer || "");
    }).catch((e) => toast.error(errMsg(e)));
    return () => { audio.stop(); };
  }, [lessonId]); // eslint-disable-line

  const screens = L?.screens || [];
  const screen = screens[idx];
  const url = (s) => mediaUrl(s?.audio_url);

  useEffect(() => {
    if (!started || !screen) return;
    api.post(`/lessons/${lessonId}/visit`, { screen_no: screen.screen_no }).catch(() => {});
    audio.preload(url(screens[idx + 1]));
    if (screens[idx + 1]?.image_url) { const im = new Image(); im.src = mediaUrl(screens[idx + 1].image_url); }
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [idx, started]); // eslint-disable-line

  const start = () => {
    unlockAudioContext();
    const found = screens.findIndex((s) => s.screen_no === L.progress.last_screen);
    const resumeAt = !L.progress.completed && found > 0 ? found : 0;
    setIdx(resumeAt);
    setStarted(true);
    setAuto(mode === "audio");
    audio.load(url(screens[resumeAt]), mode === "audio");
  };

  const go = useCallback((n, play) => {
    if (n < 0 || n >= screens.length) return;
    setIdx(n);
    audio.switchTo(url(screens[n]), play ?? mode === "audio");
  }, [screens, mode, audio]);

  const changeMode = (m) => {
    setMode(m);
    updateProfile({ audio_mode: m }).catch(() => {});
    if (m === "read") { setAuto(false); audio.stop(); }
    else if (started && !audio.playing) audio.play();
  };

  const isExerciseScreen = (s) => L?.exercise && s?.screen_no === 6;
  endedRef.current = () => {
    if (!auto) return;
    if (idx >= screens.length - 1) return setAuto(false);
    if (isExerciseScreen(screen) && !saved) {
      setAuto(false);
      toast.info("Write and save your answer, then press Start to continue");
      return;
    }
    setTimeout(() => go(idx + 1, true), 700);
  };

  const running = auto && (audio.playing || audio.loading);
  const toggleAuto = () => {
    if (running) { setAuto(false); audio.toggle(); return; }
    setAuto(true);
    if (mode === "read") { setMode("audio"); updateProfile({ audio_mode: "audio" }).catch(() => {}); }
    const atEnd = audio.dur && audio.time >= audio.dur - 0.3;
    if ((atEnd || audio.error || !screen.audio_url) && idx < screens.length - 1) go(idx + 1, true);
    else audio.play();
  };

  const saveAnswer = async () => {
    setBusy(true);
    try {
      await api.put(`/businesses/${L.business_id}/answers`, { field: L.exercise.field, value: answer, lesson_id: lessonId });
      setSaved(answer.trim());
      haptic(15);
      toast.success(t("Saved to your business"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/complete`);
      await audio.stop();
      if (user.sound_effects !== false) playChime();
      haptic(30);
      setDone(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "exercise_required") {
        toast.info("Save your answer to complete this lesson");
        const exIdx = screens.findIndex((s) => s.screen_no === 6);
        go(exIdx >= 0 ? exIdx : 0);
      } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const continueAfter = () => {
    if (done.stage_complete && !stageCeleb) return setStageCeleb(true);
    nav(actionLink(done.next_action));
  };

  if (!L) return <div className="min-h-screen bg-[#FBF8F2]"><Loader /></div>;

  if (!started) {
    return (
      <div className="h-[100dvh] bg-[#FBF8F2] flex flex-col pt-safe" data-testid="lesson-intro">
        <div className="flex items-center justify-between px-4 h-14 shrink-0">
          <Link to={`/stage/${encodeURIComponent(L.stage)}`} className="h-10 w-10 rounded-full bg-white border border-[#EADEC9] flex items-center justify-center" data-testid="lesson-close-btn"><X className="h-5 w-5" /></Link>
          <ModeToggle mode={mode} onChange={setMode} t={t} />
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-xl w-full mx-auto px-5 pb-6">
            {screens[0]?.image_url && <img src={mediaUrl(screens[0].image_url)} alt="" className="w-full aspect-video object-cover rounded-3xl bg-[#F5F0E6] fade-up" />}
            <div className="sb-eyebrow mt-5">{L.stage_name} · Lesson {L.order} of {L.stage_lesson_count}</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mt-2" data-testid="lesson-intro-title">{L.title}</h1>
            <p className="text-[#5C6B73] mt-2 leading-relaxed text-[15px]">{L.objective}</p>
            <div className="flex gap-4 mt-3 text-xs font-semibold text-[#5C6B73]">
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{L.est_min} min</span>
              <span>{screens.length} screens</span>
              {L.exercise && <span>1 action</span>}
              {L.progress.completed && <span className="text-[#1DB99A] flex items-center gap-1"><Check className="h-3.5 w-3.5" />Completed</span>}
            </div>
          </div>
        </div>
        <div className="shrink-0 border-t border-[#EADEC9] bg-white/95 backdrop-blur-md pb-safe">
          <div className="max-w-xl mx-auto px-4 py-3">
            <button onClick={start} className="sb-btn-primary w-full text-base min-h-[52px]" data-testid="start-lesson-btn">
              {mode === "audio" ? <Volume2 className="h-5 w-5" /> : <BookOpen className="h-5 w-5" />}
              {L.progress.last_screen > 1 && !L.progress.completed ? "Resume lesson" : t("Start Lesson")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const last = idx === screens.length - 1;
  const showExercise = isExerciseScreen(screen);
  return (
    <div className="h-[100dvh] bg-[#FBF8F2] flex flex-col pt-safe" data-testid="lesson-player">
      <div className="px-4 pt-2 max-w-xl w-full mx-auto">
        <div className="flex items-center gap-3 h-12">
          <Link to={`/stage/${encodeURIComponent(L.stage)}`} className="h-10 w-10 shrink-0 rounded-full bg-white border border-[#EADEC9] flex items-center justify-center" data-testid="lesson-exit-btn"><X className="h-5 w-5" /></Link>
          <div className="flex-1 min-w-0 text-sm font-bold truncate" data-testid="lesson-title">{L.title}</div>
          <ModeToggle mode={mode} onChange={changeMode} t={t} />
        </div>
        <div className="flex gap-1 mt-2" data-testid="lesson-segments">
          {screens.map((s, i) => <div key={s.screen_no} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= idx ? "bg-[#1DB99A]" : "bg-[#EADEC9]"}`} />)}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div key={idx} className="max-w-xl w-full mx-auto px-4 py-4 space-y-4 fade-up">
          {screen.image_url && <img src={mediaUrl(screen.image_url)} alt="" className="w-full aspect-[16/9] object-cover rounded-2xl bg-[#F5F0E6]" data-testid="lesson-screen-image" />}
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-extrabold" data-testid="lesson-screen-title">{screen.title}</h2>
            <span className="text-xs font-bold text-[#8C9BA5] tabular-nums" data-testid="lesson-screen-counter">{idx + 1} / {screens.length}</span>
          </div>
          {L.regulatory && (
            <div className="flex items-center gap-2 text-[11px] font-semibold text-[#179B80]" data-testid="official-source-label">
              <ShieldCheck className="h-4 w-4" />Based on official Mauritius sources
              <Link to={`/sources#${L.lesson_id}`} className="underline ml-auto" data-testid="view-official-source-link">View official source</Link>
            </div>
          )}
          <AudioBar audio={audio} hasAudio={!!screen.audio_url} estSec={screen.audio_sec} t={t} />
          <RichText text={screen.content} className="text-[15px] leading-relaxed text-[#1E2B3C]" />
          {showExercise && <Exercise ex={L.exercise} value={answer} onChange={setAnswer} t={t} />}
        </div>
      </div>

      <div className="border-t border-[#EADEC9] bg-white/95 backdrop-blur-md pb-safe">
        {showExercise && <div className="max-w-xl mx-auto px-4 pt-3"><SaveAnswerButton value={answer} saved={saved} onSave={saveAnswer} busy={busy} t={t} /></div>}
        <div className="max-w-xl mx-auto px-4 py-3 flex gap-3">
          <button onClick={() => go(idx - 1)} disabled={idx === 0} className="sb-btn-ghost flex-1 px-2" data-testid="lesson-back-btn"><ArrowLeft className="h-4 w-4" /><span className="hidden min-[380px]:inline">{t("Back")}</span></button>
          <button onClick={toggleAuto} className={`sb-btn shrink-0 rounded-full px-5 ${running ? "bg-[#E8F8F4] text-[#179B80] border-2 border-[#1DB99A]" : "bg-[#1DB99A] text-white"}`} data-testid="lesson-autoplay-btn" aria-label={running ? "Pause lesson" : "Start lesson autoplay"}>
            {running ? <><Pause className="h-4 w-4" fill="currentColor" />Pause</> : <><Play className="h-4 w-4" fill="currentColor" />Start</>}
          </button>
          {last
            ? <button onClick={complete} disabled={busy} className="sb-btn-primary flex-1 px-2" data-testid="lesson-complete-btn"><Check className="h-4 w-4" />{t("Complete lesson")}</button>
            : <button onClick={() => go(idx + 1)} className="sb-btn-navy flex-1 px-2" data-testid="lesson-next-btn">{t("Next")}<ArrowRight className="h-4 w-4" /></button>}
        </div>
      </div>

      {done && !stageCeleb && (
        <Celebration testid="lesson-complete-modal">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#1DB99A] text-white flex items-center justify-center"><Check className="h-8 w-8" strokeWidth={3} /></div>
          <div className="sb-eyebrow mt-5 text-[#179B80]">✓ Lesson complete</div>
          <h2 className="text-2xl font-extrabold mt-2">{t("Lesson complete")}</h2>
          <p className="text-sm text-[#5C6B73] mt-2">{L.objective}</p>
          <div className="mt-5 text-left rounded-xl bg-white border border-[#EADEC9] p-3">
            <div className="flex justify-between text-xs font-bold mb-1.5"><span>{L.stage_name}</span><span>{done.stage.lessons_done}/{done.stage.lessons_total} lessons</span></div>
            <div className="h-2 rounded-full bg-[#EADEC9] overflow-hidden"><div className="h-full bg-[#1DB99A] transition-[width] duration-700" style={{ width: `${done.stage.pct}%` }} /></div>
            <div className="text-[11px] text-[#5C6B73] mt-2">Business progress: <b data-testid="lesson-complete-overall">{done.overall}%</b></div>
          </div>
          <button onClick={continueAfter} className="sb-btn-primary w-full mt-5" data-testid="lesson-complete-continue-btn">{t("CONTINUE")}</button>
        </Celebration>
      )}
      {stageCeleb && (
        <Celebration testid="stage-complete-modal">
          <div className="sb-eyebrow text-[#179B80]">Stage complete</div>
          <h2 className="text-3xl font-extrabold mt-2">{L.stage_name} COMPLETE ✓</h2>
          <p className="text-sm text-[#5C6B73] mt-2">You've finished every lesson and task in this stage.</p>
          <div className="mt-5 rounded-xl bg-[#1E2B3C] text-white p-4"><div className="text-[11px] text-white/60">Stage Output</div><div className="font-display font-bold text-lg">{done.stage.output}</div></div>
          <Link to={`/stage/${encodeURIComponent(L.stage)}/output`} className="sb-btn-ghost w-full mt-4" data-testid="view-stage-output-btn">View {done.stage.output}</Link>
          <button onClick={() => nav(actionLink(done.next_action))} className="sb-btn-primary w-full mt-2" data-testid="continue-next-stage-btn">
            {done.next_action?.type === "next_stage" ? `Continue to ${done.next_action.stage}` : t("CONTINUE")}</button>
        </Celebration>
      )}
    </div>
  );
}
